import { Box, CircularProgress, LinearProgress, styled, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// show progress bar from 0 to 98% during 10 minutes, than stack at 98% if the issues wasn't resolved earlier
const TOTAL_MS_TO_FINISH = 10 * 60 * 1000; // 10 minutes
const MAX_BEFORE_HEALTHY = 98;
const UPDATE_EVERY_MS = 250;

const Root = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2.5),
  maxWidth: 500,
  width: '100%',
  textAlign: 'center',
  padding: theme.spacing(4),
  borderRadius: 16,
  [theme.breakpoints.up('sm')]: {
    border: `1px solid ${theme.palette?.custom?.OV?.sys?.outlineVariant}`,
  },
}));

export const HealthErrorPage = () => {
  const [progress, setProgress] = useState(4);
  const startedAtRef = useRef<number>(Date.now());
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    const timer = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      const ratio = Math.min(1, elapsed / TOTAL_MS_TO_FINISH);
      const next = Math.min(MAX_BEFORE_HEALTHY, Math.floor(ratio * MAX_BEFORE_HEALTHY));
      setProgress((prev) => (next > prev ? next : prev));
    }, UPDATE_EVERY_MS);

    return () => clearInterval(timer);
  }, []);

  return (
    <Root>
      <CircularProgress color="primary" />

      <Box>
        <Typography variant="h5" fontWeight={900}>
          ProVision is Rebooting
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" fontSize="14px">
          Looks like something went sideways.
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
        We know your customers rely on you, and you rely on us. Our team has been notified and
        is working to resolve this. This is our top priority.
      </Typography>

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          maxWidth: 560,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Rebooting systems…
        </Typography>
        <Typography variant="body2" color="black" fontWeight={900}>
          {`${Math.round(progress)}%`}
        </Typography>
      </Box>
      <Box width="100%">
        <LinearProgress
          variant="determinate"
          color="primary"
          value={progress}
          sx={{ height: '0.5rem', borderRadius: '10px' }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        We&apos;re automatically checking for recovery. You will be redirected once we&apos;re
        back online.
      </Typography>
    </Root>
  );
};
